import type { BrandingSettings, HeaderSettings, FooterSettings } from 'types';

export const DEFAULT_HEADER_SETTINGS: HeaderSettings = {
    enabled: false,
    content: '',
    showOnFirstPage: true,
    textAlign: 'center',
    fontSize: 10,
    textColor: '#666666',
    paddingTop: 12,
    paddingBottom: 12,
};

export const DEFAULT_FOOTER_SETTINGS: FooterSettings = {
    enabled: false,
    content: '',
    showOnFirstPage: true,
    pageNumbers: {
        enabled: true,
        position: 'right',
        format: 'page-of-total',
        prefix: 'Page ',
    },
    textAlign: 'center',
    fontSize: 9,
    textColor: '#666666',
    paddingTop: 12,
    paddingBottom: 12,
};

export const FONT_FAMILIES = [
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Georgia',
    'Verdana',
    'Courier New',
    'Calibri',
    'Garamond',
    'Trebuchet MS',
    'Comic Sans MS',
] as const;

export const LINE_HEIGHT_OPTIONS = [
    { value: 1.0, label: 'Single (1.0)' },
    { value: 1.15, label: 'Tight (1.15)' },
    { value: 1.5, label: 'Normal (1.5)' },
    { value: 1.75, label: 'Relaxed (1.75)' },
    { value: 2.0, label: 'Double (2.0)' },
] as const;

export const CURRENCY_OPTIONS = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'CAD', label: 'CAD (C$)' },
    { value: 'AUD', label: 'AUD (A$)' },
    { value: 'JPY', label: 'JPY (¥)' },
    { value: 'CHF', label: 'CHF (CHF)' },
    { value: 'CNY', label: 'CNY (¥)' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'BRL', label: 'BRL (R$)' },
] as const;

export const PAGE_SIZES = {
    letter: { width: '8.5in', height: '11in', label: 'Letter (8.5×11)' },
    a4: { width: '8.27in', height: '11.69in', label: 'A4 (8.27×11.69)' },
    legal: { width: '8.5in', height: '14in', label: 'Legal (8.5×14)' },
} as const;

export const DEFAULT_BRANDING: BrandingSettings = {
    pageSize: 'letter',
    primaryColor: '#000000',
    secondaryColor: '#6366f1',
    textColor: '#1f2937',
    backgroundColor: '#ffffff',
    fontFamily: 'Arial',
    headingFontSize: 18,
    bodyFontSize: 12,
    lineHeight: 1.5,
    marginTop: 1,
    marginBottom: 1,
    marginLeft: 1,
    marginRight: 1,
    paragraphSpacingBefore: 0,
    paragraphSpacingAfter: 6,
    header: DEFAULT_HEADER_SETTINGS,
    footer: DEFAULT_FOOTER_SETTINGS,
};

/**
 * Calculate the relative luminance of a color
 * Based on WCAG 2.0 guidelines
 */
function getLuminance(color: string): number {
    // Remove # if present
    const hex = color.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    // Apply gamma correction
    const [rGamma, gGamma, bGamma] = [r, g, b].map((val) => {
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });

    // Calculate relative luminance
    return 0.2126 * rGamma + 0.7152 * gGamma + 0.0722 * bGamma;
}

/**
 * Calculate contrast ratio between two colors
 * WCAG 2.0 requires a minimum contrast ratio of 4.5:1 for normal text
 */
export function hasGoodContrast(foreground: string, background: string): boolean {
    const foregroundLum = getLuminance(foreground);
    const backgroundLum = getLuminance(background);

    const lighter = Math.max(foregroundLum, backgroundLum);
    const darker = Math.min(foregroundLum, backgroundLum);

    const contrastRatio = (lighter + 0.05) / (darker + 0.05);

    // Return true if contrast ratio meets WCAG AA standard (4.5:1)
    return contrastRatio >= 4.5;
}
